// THE ONE SSE CONSUMER LOOP: dial, drain, decide, back off, repeat.
//
// It was two loops. `appendLogStream.ts` carried one inline for the two
// append-log views, and `account-stream-loop.ts` + `account-stream-frames.ts`
// carried another for the multiplexed account feed. They already shared the
// framing (`sseFrames.ts`) and nothing else — which is how the same per-chunk
// dispatch defect came to live in both drains (see {@link readSseBody}).
//
// What is NOT collapsed is the reconnect posture. See {@link ReconnectPolicy}:
// the callers answer "a connection just closed" differently, on purpose, and the
// answer is a value the caller passes — never a default this file picks.

import { SseEvent } from '@developerz/domain';
import { DeadlineExceededError, withDeadline } from '@developerz/kit';
import { clientErrorReport, reportClientError } from './client-errors';
import { markSessionExpired } from './session-expiry';
import { abortableDelay, reconnectDelayMs, retryAfterMs, SSE_HEALTHY_OPEN_MS } from './sseBackoff';
import { parseSseFrame, type SseFrame } from './sseFrames';

/**
 * What a caller wants done when a connection closes. Both postures are
 * deliberate and neither is the other's default.
 *
 * `announced_only` is an APPEND LOG (`/v1/sessions/:id/events`,
 * `/v1/fleet/runs/:runId/events`). The log has a terminal state, so the only
 * ROUTINE reconnect is the route's ~10-minute duration cap, which announces
 * itself with `bye`. Everything else — a bare close, a 404, a 503 — is a fault a
 * human should see behind a Retry button: a view that silently re-dials a stream
 * failing every 3 s shows a spinner over a broken run for ever.
 *
 * `always` is the multiplexed account feed (`/v1/streams`) — one long-lived
 * connection per tab, no terminal state, where an api pod recycling is routine.
 * It reconnects on every close and re-patches idempotently, so it needs the
 * ladder: growth to bound the spend, jitter to break the herd (`sseBackoff.ts`
 * states why a constant pace turns a roll into a self-inflicted outage).
 *
 * The pacing rides the posture rather than sitting beside it as a second knob.
 * `announced_only` only ever reconnects on a recycle whose instant the SERVER
 * chose, so those connections are already staggered and there is no herd for
 * jitter to break; it takes the published `retry:` flat, which is what its two
 * views have always done.
 */
export type ReconnectPolicy =
  | { readonly kind: 'announced_only' }
  | { readonly kind: 'always'; readonly random: () => number };

/**
 * How one connection ended. Seven kinds and not a boolean, because each earns a
 * different answer — and a refusal carries its status rather than collapsing
 * "this run does not exist" and "the socket dropped" into one `null`.
 */
export type SseOutcome =
  /** The append log ENDED (`done`). Terminal under every policy. */
  | { readonly kind: 'ended' }
  /** The server closed on purpose at its duration cap (`bye`). Always reconnects. */
  | { readonly kind: 'recycled' }
  /** Closed with no announcement, or the dial itself threw. A drop. */
  | { readonly kind: 'dropped' }
  /** A non-retryable 4xx: bad id, bad scope, bad topics. Hammering changes nothing. */
  | { readonly kind: 'refused'; readonly status: number }
  /** A 5xx, a 429, or an ok response with no body. `retryAfterMs` is the server's own wait. */
  | { readonly kind: 'unavailable'; readonly status: number; readonly retryAfterMs: number | null }
  /** The caller's signal aborted — unmount, org switch, retry. Nobody is listening. */
  | { readonly kind: 'aborted' }
  /**
   * The caller's `onFrames` THREW: a bug in this app, not a fault of the stream.
   * Reported (`reportClientError`) and terminal under every policy — re-dialling
   * replays the snapshot that crashed it (#5608 slice 17 row 22).
   */
  | { readonly kind: 'crashed'; readonly error: unknown };

export type SseSubscribeInput = {
  readonly policy: ReconnectPolicy;
  readonly signal: AbortSignal;
  /** Open ONE connection. Re-invoked per attempt, so a caller can move its resume header. */
  readonly dial: () => Promise<Response>;
  /** Every data frame from ONE network chunk, in order. See {@link readSseBody}. */
  readonly onFrames: (frames: readonly SseFrame[]) => void;
  /** Fired once a response is a live stream, before any frame of it. */
  readonly onOpen: () => void;
  /** Fired before each reconnect wait, with whatever ended the connection. */
  readonly onReconnect: (outcome: SseOutcome) => void;
  /** The ladder's base, until a connection publishes its own `retry:`. */
  readonly startingRetryMs: number;
  /** Monotonic ms clock timing how long a connection stayed up. `performance.now` unless a test injects one. */
  readonly now?: () => number;
};

/**
 * How long a connection may go without a single byte before it is dead: three
 * missed beats. Every SSE route writes `: heartbeat` every 5 s (`HEARTBEAT_MS`,
 * `apps/api/src/middleware/sse-stream.ts` — restated, since apps never import
 * apps). The read loop used to await `reader.read()` unbounded, so a half-open
 * socket (a NAT timeout, a VPN drop) kept the pill `live` and the REST polls
 * suspended until the transport noticed (#5608 slice 17 row 17).
 */
export const SSE_SILENCE_MS = 3 * 5_000;

/**
 * One `read()`, or `null` when nothing at all arrived within {@link SSE_SILENCE_MS}
 * — kit's one deadline, its `DeadlineExceededError` read as silence. Losing the
 * race leaves the read pending; `readSseBody`'s `finally` cancels the reader.
 */
async function readOrSilence<T>(
  reader: ReadableStreamDefaultReader<T>,
): Promise<Awaited<ReturnType<ReadableStreamDefaultReader<T>['read']>> | null> {
  try {
    return await withDeadline(reader.read(), SSE_SILENCE_MS, 'sse body read');
  } catch (error) {
    if (error instanceof DeadlineExceededError) return null;
    throw error;
  }
}

/** Whether `outcome` reconnects under `policy` — the whole posture, in one place. */
export function shouldReconnect(policy: ReconnectPolicy, outcome: SseOutcome): boolean {
  if (outcome.kind === 'recycled') return true;
  if (policy.kind === 'announced_only') return false;
  return outcome.kind === 'dropped' || outcome.kind === 'unavailable';
}

/** How long to wait after `outcome`, given where the ladder currently stands. */
function waitMs(
  policy: ReconnectPolicy,
  outcome: SseOutcome,
  pacing: { readonly attempt: number; readonly baseMs: number },
): number {
  if (policy.kind === 'announced_only') return pacing.baseMs;
  return reconnectDelayMs({
    ...pacing,
    retryAfterMs: outcome.kind === 'unavailable' ? outcome.retryAfterMs : null,
    random: policy.random,
  });
}

/** Split every complete `\n\n` block out of `buf`; `rest` is the tail still arriving. */
function splitFrames(buf: string): { frames: SseFrame[]; rest: string } {
  const frames: SseFrame[] = [];
  let rest = buf;
  for (;;) {
    const sep = rest.indexOf('\n\n');
    if (sep === -1) return { frames, rest };
    const frame = parseSseFrame(rest.slice(0, sep));
    rest = rest.slice(sep + 2);
    if (frame !== null) frames.push(frame);
  }
}

/**
 * Separate what the LOOP reads from a chunk (the pacing, and the two frames that
 * end a connection) from what a VIEW reads (everything else). A pacing-only
 * block — the heartbeat's first beat — is consumed here and never forwarded, so
 * no consumer ever sees a `data: ''` frame.
 */
function partitionChunk(
  frames: readonly SseFrame[],
  onRetry: (ms: number) => void,
): { data: SseFrame[]; ending: SseOutcome | null } {
  const data: SseFrame[] = [];
  let ending: SseOutcome | null = null;
  for (const frame of frames) {
    if (frame.retry !== undefined) onRetry(frame.retry);
    if (frame.event === SseEvent.Bye) ending = { kind: 'recycled' };
    else if (frame.event === SseEvent.Done) ending = { kind: 'ended' };
    else if (frame.data !== '') data.push(frame);
  }
  return { data, ending };
}

/**
 * Drain one body to its end, dispatching frames ONE BATCH PER NETWORK CHUNK.
 *
 * A `reader.read()` yields whatever TCP handed the runtime, which on a backfill
 * is routinely many frames at once. Both former drains dispatched them one at a
 * time, so a caller's setter ran once per row — a chunk of 200 rows repainted a
 * growing list 200 times where one render would have shown it. The batch is the
 * unit now, and a chunk carrying no data frame (a heartbeat) dispatches nothing.
 *
 * Every chunk re-arms the silence watchdog — a heartbeat comment is a chunk with
 * no frame, so it is life too. {@link SSE_SILENCE_MS} without one ends the
 * attempt as `dropped`: the same answer as a close, since a socket nobody writes
 * to is one.
 *
 * The reader is cancelled on EVERY exit — end of body, terminal frame, silence,
 * or the abort that throws out of `read()`. A reader left locked to an abandoned
 * response is one socket per reconnect that nothing ever closes.
 */
async function readSseBody(
  body: NonNullable<Response['body']>,
  onFrames: SseSubscribeInput['onFrames'],
  onRetry: (ms: number) => void,
): Promise<SseOutcome> {
  const reader = body.pipeThrough(new TextDecoderStream()).getReader();
  let buf = '';
  try {
    for (;;) {
      const read = await readOrSilence(reader);
      if (read === null || read.done) return { kind: 'dropped' };
      const { value } = read;
      const split = splitFrames(buf + value);
      buf = split.rest;
      const { data, ending } = partitionChunk(split.frames, onRetry);
      // Its own catch: the one around this whole read (in `attemptSse`) means
      // "the socket failed", and a consumer's throw filed there was retried as a
      // drop forever, unreported.
      if (data.length > 0) {
        try {
          onFrames(data);
        } catch (error) {
          reportClientError(clientErrorReport('error', error));
          return { kind: 'crashed', error };
        }
      }
      if (ending !== null) return ending;
    }
  } finally {
    // Started, never awaited. Cancelling the readable end of a `pipeThrough`
    // settles only once the cancel has walked back up the pipe to the socket,
    // and on a body the server is still writing that can outlive the decision
    // to stop reading it — awaiting here would hold the reconnect behind the
    // connection it is replacing. A cancel on an already-closed body is a spec
    // no-op, and one on an aborted body rejects; neither is a failure.
    void reader.cancel().catch(() => {});
  }
}

/** Dial once and drain what comes back. Classification is shared; the response to it is not. */
async function attemptSse(
  input: SseSubscribeInput,
  onRetry: (ms: number) => void,
): Promise<SseOutcome> {
  let res: Response;
  try {
    res = await input.dial();
  } catch {
    // An abort throws here too; `sseSubscribe` re-reads its signal to tell them apart.
    return { kind: 'dropped' };
  }
  if (res.status >= 400 && res.status < 500 && res.status !== 429) {
    // THE SECOND HALF OF THE 401 LATCH. `session-expiry.ts` states the
    // invariant as "a 401 ANYWHERE means the credential is gone", and sets it
    // at `apiFetch` — the one client every REST call goes through. These three
    // live views are the calls that do NOT: they dial with raw `fetch` so the
    // `dev_pat_` bearer can ride along, which native EventSource cannot carry.
    // Without this line an idle tab whose `dz_session` expired answered 401 on
    // the account stream's next recycle, painted a red pill, and sat there —
    // staleness has no interval (`query.ts`) and a tab nobody refocuses fires no
    // focus refetch, so no REST call latched it and nothing routed to <Login/>.
    //
    // 401 ONLY. A 403 is a live credential whose tenant claim or scope was
    // refused and a 404 is a bad id (`middleware/auth.ts`: "a 403, never a
    // 401 — the credential is fine, the tenant claim is not"); signing the
    // user out over either would be a worse bug than the one this fixes.
    if (res.status === 401) markSessionExpired();
    return { kind: 'refused', status: res.status };
  }
  if (!res.ok || res.body === null) {
    return { kind: 'unavailable', status: res.status, retryAfterMs: retryAfterMs(res) };
  }
  input.onOpen();
  try {
    return await readSseBody(res.body, input.onFrames, onRetry);
  } catch {
    // A rejected `read()` is how an ABORT reaches this loop: the fetch tears its
    // own body down, and the pending read rejects rather than resolving. A socket
    // error looks the same from here, which is why both spell `dropped` and
    // `sseSubscribe` re-reads its signal to tell them apart. Letting it through
    // instead would surface as an unhandled rejection on every unmount.
    return { kind: 'dropped' };
  }
}

/**
 * Subscribe until the log ends, the server refuses in a way retrying cannot fix,
 * or `signal` aborts — whichever the caller's {@link ReconnectPolicy} reaches
 * first. Resolves with the outcome that stopped it, for the caller to render.
 *
 * Two things RESET the ladder. An announced recycle: a server closing on purpose
 * at its duration cap is proof it is healthy, and climbing on it would walk a
 * perfectly good tab toward the cap over the course of an afternoon. And a
 * connection that stayed up {@link SSE_HEALTHY_OPEN_MS} before it dropped: a tab
 * dropped by sleep/wake or a proxy idle timeout is not a failing server, and
 * climbing on it left the bell dark 30-60 s after every drop (#4800). Everything
 * else — a refusal, a 5xx, a connection that opened and died at once — climbs.
 *
 * Nothing outlives the call. The only timer is `abortableDelay`, which clears its
 * own on abort, and the only reader is cancelled by `readSseBody`'s `finally`.
 */
export async function sseSubscribe(input: SseSubscribeInput): Promise<SseOutcome> {
  // Held ACROSS reconnects: `retry:` rides a connection's first write, so one
  // that dies before any frame reuses the last pace the server asked for rather
  // than falling back to the default.
  let baseMs = input.startingRetryMs;
  let attempt = 0;
  const now = input.now ?? (() => performance.now());
  while (!input.signal.aborted) {
    let openedAt: number | null = null;
    const outcome = await attemptSse(
      {
        ...input,
        onOpen: () => {
          openedAt = now();
          input.onOpen();
        },
      },
      (ms) => {
        baseMs = ms;
      },
    );
    if (input.signal.aborted) return { kind: 'aborted' };
    if (!shouldReconnect(input.policy, outcome)) return outcome;
    const cameUp = openedAt !== null && now() - openedAt >= SSE_HEALTHY_OPEN_MS;
    attempt = outcome.kind === 'recycled' || cameUp ? 0 : attempt + 1;
    input.onReconnect(outcome);
    await abortableDelay(waitMs(input.policy, outcome, { attempt, baseMs }), input.signal);
  }
  return { kind: 'aborted' };
}
